/**
 * IRR Calculator Utility
 * Calculates Internal Rate of Return for real estate investments
 */

/**
 * Calculate NPV (Net Present Value) for a given discount rate
 * @param {number} initialInvestment - Initial cash outflow (negative)
 * @param {number[]} cashFlows - Array of annual cash flows
 * @param {number} rate - Discount rate (as decimal, e.g., 0.1 for 10%)
 * @returns {number} Net Present Value
 */
function calculateNPV(initialInvestment, cashFlows, rate) {
  let npv = -initialInvestment; // Initial investment is negative

  for (let i = 0; i < cashFlows.length; i++) {
    npv += cashFlows[i] / Math.pow(1 + rate, i + 1);
  }

  return npv;
}

/**
 * Calculate IRR using Newton-Raphson method
 * @param {number} initialInvestment - Initial cash outflow
 * @param {number[]} cashFlows - Array of annual cash flows
 * @param {number} guess - Initial guess for IRR (default 0.1)
 * @param {number} tolerance - Convergence tolerance (default 0.0001)
 * @param {number} maxIterations - Maximum iterations (default 100)
 * @returns {number} IRR as percentage (e.g., 8.5 for 8.5%)
 */
export function calculateIRR(initialInvestment, cashFlows, guess = 0.1, tolerance = 0.0001, maxIterations = 100) {
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    const npv = calculateNPV(initialInvestment, cashFlows, rate);

    // Check for convergence
    if (Math.abs(npv) < tolerance) {
      return rate * 100; // Return as percentage
    }

    // Calculate derivative (NPV')
    let derivative = 0;
    for (let j = 0; j < cashFlows.length; j++) {
      derivative -= (j + 1) * cashFlows[j] / Math.pow(1 + rate, j + 2);
    }

    // Newton-Raphson update
    rate = rate - npv / derivative;

    // Prevent extreme values
    if (rate < -0.99) rate = -0.99;
    if (rate > 10) rate = 10;
  }

  // If not converged, return NaN
  return NaN;
}

/**
 * Generate year-by-year cash flow projection
 * @param {Object} params - Investment parameters
 * @returns {Object} Year-by-year projection and IRR
 */
export function generateCashFlowProjection(params) {
  const {
    propertyPrice,
    monthlyRent, // The AI predicted rent -- one source, see IRRModal
    monthlyCarryingCosts,
    totalOutOfPocket, // New: Use Total Out of Pocket as initial investment
    monthlyMortgagePayment, // New: Use saved mortgage payment (for initial I/O period)
    mortgageRate = 7, // New: Use saved mortgage rate or default
    mortgageTerm = 30, // New: Use saved mortgage term or default (always 30 years)
    // Interest-only over a ten-year hold is the house assumption, and it is the DEFAULT here
    // so that every surface quoting an IRR quotes the same deal. Five of them used to
    // disagree on both: the report followed the listing's saved payment_type while the Rental
    // Value Analysis and Property Details hardcoded I/O, and those ran 7 years against the
    // report's 10. One apartment, three numbers, no way to tell which was meant.
    paymentType = "I/O",
    downPaymentPercent = 25, // New: Use saved down payment percent
    rentGrowthRate = 5, // compounded on the RENT -- see the loop below
    propertyAppreciationRate = 6, // percent per year (hardcoded)
    years = 10,
    // Property management, charged as a percent of the rent actually collected and only from
    // managementFeeStartYear on -- the first year is self-managed while the unit is being set
    // up. 0 (the default) is "no fee", which keeps every existing caller unchanged.
    managementFeePercent = 0,
    managementFeeStartYear = 2,
  } = params;

  // Use Total Out of Pocket as initial investment
  const initialInvestment = totalOutOfPocket;

  // Calculate mortgage amount directly from property price and down payment
  const downPayment = propertyPrice * (downPaymentPercent / 100);
  const mortgageAmount = propertyPrice - downPayment;

  const monthlyInterestRate = mortgageRate / 100 / 12;
  const numberOfPayments = mortgageTerm * 12;

  const yearlyData = [];
  let currentPropertyValue = propertyPrice;
  const sellingCostPercent = 6.825; // Cost of selling the property

  for (let year = 1; year <= years; year++) {
    // THE RENT GROWS. It sounds too obvious to state, but this is the fix: the projection
    // used to hold the rent almost still and grow the CASH FLOW by rentGrowthRate instead,
    // then back-solve whatever rent would have produced it. Two things followed from that.
    //
    // The growth compounded on `|year-1 cash flow|`, so the step was 5% of a number that is
    // near zero on a leveraged deal -- $42/year on listing 1356079 -- and the rent it implied
    // moved $4,995 -> $5,021 over a DECADE. The closer a deal sat to break-even the less rent
    // growth it got, and at exactly break-even the rent was frozen for ever, at any growth
    // rate. On that listing it understated ten-year cash flow by 54%: $102,093 against
    // $223,497, on identical financing.
    //
    // It also made the management fee incoherent. The fee is 5% of GROSS RENT (~$3,000/yr)
    // while the growth credited 5% of CASH FLOW (~$42/yr), so year 2 fell off a cliff and
    // then crept back at a rate that would have taken 71 years to recover.
    const currentRent = monthlyRent * Math.pow(1 + rentGrowthRate / 100, year - 1);
    const annualRentIncome = currentRent * 12;

    // Expenses. The mortgage payment is legitimately flat (fixed rate, and on I/O the payment
    // never changes); the carrying costs are flat BY ASSUMPTION. HOA and taxes do not really
    // stand still for ten years -- that is a known simplification, not a claim.
    const annualCarryingCosts = monthlyCarryingCosts * 12;
    const annualMortgagePayment = monthlyMortgagePayment * 12;

    // Property management: a percent of the rent collected, from managementFeeStartYear on.
    // Year 1 is the lease-up and is not charged.
    const managementFee = year >= managementFeeStartYear
      ? annualRentIncome * (managementFeePercent / 100)
      : 0;

    const annualCashFlow =
      annualRentIncome - annualCarryingCosts - annualMortgagePayment - managementFee;

    // Calculate property appreciation
    const yearStartValue = currentPropertyValue;
    currentPropertyValue = currentPropertyValue * (1 + propertyAppreciationRate / 100);
    const appreciation = currentPropertyValue - yearStartValue;

    // Calculate mortgage balance at end of year
    let mortgageBalance = 0;
    if (mortgageAmount > 0) {
      if (paymentType === "I/O") {
        // Interest Only: the balance never changes. The principal comes back only out of the
        // sale proceeds.
        mortgageBalance = mortgageAmount;
      } else {
        // P&I: the balance is the present value of the payments still to come.
        const paymentsToDate = year * 12;
        const remainingPayments = numberOfPayments - paymentsToDate;

        if (remainingPayments > 0) {
          mortgageBalance = monthlyMortgagePayment * (Math.pow(1 + monthlyInterestRate, remainingPayments) - 1) /
            (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, remainingPayments));
        }
      }
    }

    // Calculate equity (property value - mortgage balance)
    // In final year, subtract selling costs from property value
    let equity;
    if (year === years) {
      const netPropertyValue = currentPropertyValue * (1 - sellingCostPercent / 100);
      equity = netPropertyValue - mortgageBalance;
    } else {
      equity = currentPropertyValue - mortgageBalance;
    }

    // What an exit IN THIS YEAR actually hands back: value, less the cost of selling, less the
    // debt. `equity` above is only net of selling costs in the FINAL year, which is right for
    // the projection's own IRR and wrong for anyone reading an earlier year as an exit -- the
    // IRR report's 7-Year Exit panel did exactly that out of a 10-year run and overstated the
    // return by 36 points. Published per year so no caller has to remember the rule.
    const equityIfSold = currentPropertyValue * (1 - sellingCostPercent / 100) - mortgageBalance;

    // For IRR calculation, we only use cash flow (not paper appreciation)
    // Unless it's the final year where we "sell" the property
    const cashFlowForIRR = year === years
      ? annualCashFlow + equity // Final year includes equity from sale (after selling costs)
      : annualCashFlow;

    yearlyData.push({
      year,
      rent: Math.round(currentRent),
      annualRentIncome: Math.round(annualRentIncome),
      annualCarryingCosts: Math.round(annualCarryingCosts),
      annualMortgagePayment: Math.round(annualMortgagePayment),
      totalExpenses: Math.round(annualCarryingCosts + annualMortgagePayment + managementFee),
      managementFee: Math.round(managementFee),
      cashFlow: Math.round(annualCashFlow),
      propertyValue: Math.round(currentPropertyValue),
      appreciation: Math.round(appreciation),
      mortgageBalance: Math.round(mortgageBalance),
      equity: Math.round(equity),
      equityIfSold: Math.round(equityIfSold),
      cashFlowForIRR: Math.round(cashFlowForIRR),
    });
  }

  // Calculate IRR
  const cashFlows = yearlyData.map(d => d.cashFlowForIRR);
  const irr = calculateIRR(initialInvestment, cashFlows);

  // Calculate total return metrics
  const totalCashFlowReceived = yearlyData.reduce((sum, d) => sum + d.cashFlow, 0);
  const finalEquity = yearlyData[yearlyData.length - 1].equity;
  const totalReturn = totalCashFlowReceived + finalEquity;
  const totalReturnPercent = ((totalReturn - initialInvestment) / initialInvestment) * 100;

  return {
    yearlyData,
    irr: isNaN(irr) ? 0 : irr,
    initialInvestment,
    totalCashFlow: totalCashFlowReceived,
    finalEquity,
    totalReturn,
    totalReturnPercent,
    mortgageAmount: Math.round(mortgageAmount),
    monthlyMortgagePayment: Math.round(monthlyMortgagePayment),
    monthlyCarryingCosts: Math.round(monthlyCarryingCosts),
    startingRent: Math.round(monthlyRent),
    sellingCostPercent,
  };
}
